// Copyright (c) StrangeBee 2023

import PreviewButton from '@Atoms/Buttons/PreviewButton/PreviewButton';
import { IconLabel } from '@Atoms/Info';
import THTag from '@Atoms/THTag/THTag';
import UserAvatar from '@Atoms/UserAvatar/UserAvatar';
import client from '@Client';
import { HTMLColors } from '@Constants/colors';
import { useLocalMessage } from '@Helpers/locale';
import { handleNotificationError, openMessage } from '@Helpers/notification';
import useCasePermissions from '@Hooks/useCasePermissions';
import { useDrawer } from '@Hooks/useDrawer';
import { THDatePicker } from '@Molecules/Inputs/THDatePicker/THDatePicker';
import { THEditable } from '@Molecules/Inputs/THEditable/THEditable';
import { THSelect } from '@Molecules/Inputs/THSelect/THSelect';
import { IsFlag } from '@Organisms/IsFlag/IsFlag';
import { computeEventList } from '@Organisms/Task/eventList';
import TaskDrawer from '@Organisms/Task/TaskDrawer/TaskDrawer';
import { DisplayEventProps } from '@Organisms/Timeline/DisplayEvent';
import { EventCard } from '@Organisms/Timeline/EventCard';
import { useListAllUsers } from '@Queries/userQueries';
import React, { ReactElement } from 'react';
import { useParams } from 'react-router';
import { OrgPermissionsEnum, TaskOutput } from 'thehive-sdk';

import './TaskContent.scss';

export const TaskContent = (props: DisplayEventProps): ReactElement => {
    const { caseId } = useParams<{ caseId: string }>();
    const i = useLocalMessage();
    const hasPermissions = useCasePermissions({
        caseId,
        permissionsNeeded: [OrgPermissionsEnum.MANAGE_TASK],
    });
    const task = props.currentEvent.details?.task;

    const { data: userList = [] } = useListAllUsers();
    const { title = '', group = '', flag = false, assignee = '' } = task || {};
    const { currentEvent, refetch } = props;

    const { visible, setVisible } = useDrawer<TaskOutput>({});

    return (
        <EventCard {...props}>
            <div className="task-content card__content">
                <div className="task-content__header card__header">
                    <div className="task-content__title">
                        <THTag value={group} outline full color={HTMLColors.BLUE} />
                        {title}
                    </div>

                    <IsFlag value={flag} />
                </div>
                {assignee && (
                    <THEditable
                        name="assignee"
                        className="card-info__user"
                        initialValues={{ assignee }}
                        label={i('common.assignee')}
                    >
                        <THSelect disabled borderless>
                            {userList?.map(({ _id, name, avatar, login }) => (
                                <THSelect.Option key={_id} value={login}>
                                    <IconLabel
                                        key={_id}
                                        icon={<UserAvatar name={name} avatar={avatar} />}
                                        label={name}
                                    />
                                </THSelect.Option>
                            ))}
                        </THSelect>
                    </THEditable>
                )}

                {currentEvent.date && (
                    <THEditable
                        name="date"
                        className="card-info__date"
                        initialValues={{ date: currentEvent.date }}
                        label={i('common.start-date')}
                    >
                        <THDatePicker disabled borderless />
                    </THEditable>
                )}

                {currentEvent.endDate && (
                    <THEditable
                        name="endDate"
                        className="card-info__date"
                        initialValues={{ endDate: currentEvent.endDate }}
                        label={i('common.end-date')}
                    >
                        <THDatePicker disabled borderless />
                    </THEditable>
                )}

                {task && (
                    <div className="card__preview">
                        <PreviewButton onClick={() => setVisible(true)} />
                    </div>
                )}

                {visible && (
                    <TaskDrawer
                        visible={visible}
                        onVisibleChange={setVisible}
                        data={task}
                        caseId={caseId}
                        hasPermissions={hasPermissions}
                        eventList={computeEventList({
                            controller: client.TaskController,
                            locale: i,
                            onSuccess: (_, message) => {
                                message && openMessage('success', message);
                                refetch?.();
                            },
                            onError: handleNotificationError,
                        })}
                    />
                )}
            </div>
        </EventCard>
    );
};
